import React from 'react'
import {
  Typography,
  Grid,
  List,
  ListItem,
  CircularProgress,
  Badge,
} from '@material-ui/core'
import PersonAvatar from 'app/components/PersonAvatar'
import * as R from 'ramda'
import clsx from 'clsx'
import { FuseScrollbars, FuseAnimateGroup } from '@fuse'
import Loader from 'app/components/Loader'
import styleList from 'app/main/messages/conversations/styleList'
import Assets from 'app/configs/assetConfigs'
import InfiniteScroll from 'react-infinite-scroll-component'
import { FormattedMessage } from 'react-intl'
import { getConversationDateTime } from 'commons/date'
import RichTextDisplay from 'app/components/richTextEditor/RichTextDisplay'
import { getAvatarPlaceHolder, isStartWithEng } from 'libs/text'
import { ConversationStatus } from 'app/main/messages/conversations/useConversation'
import { useIntl } from 'react-intl'

const MAX_WIDTH_CHAT_LIST_ICON = 80

const listItemEmpty = (classes) => {
  return (
    <ListItem className="flex">
      <div className="flex flex-1 items-center">
        <Grid container>
          <Grid item xs={12} className="text-center pb-10">
            <img
              width={MAX_WIDTH_CHAT_LIST_ICON}
              src={Assets.conversation.chatListIcon}
              alt="chat"
            />
          </Grid>
          <Grid item xs={12} className="text-center">
            <Typography className={classes.conversationsEmptyTitle}>
              <FormattedMessage id="conversations.empty.title" />
            </Typography>
            <Typography className={classes.conversationsEmptyContent}>
              <FormattedMessage id="conversations.empty.description" />
            </Typography>
          </Grid>
        </Grid>
      </div>
    </ListItem>
  )
}

const deletedPlaceholderMessageId = 'conversation.subscriberDeleted.placeholder'

const renderSubscriberName = (value, deletedPlaceholderText) => {
  if (value.conversationStatus === ConversationStatus.SUBSCRIBER_DELETED) {
    return deletedPlaceholderText
  } else {
    return R.compose(
      R.join(' '),
      R.filter(R.complement(R.isNil)),
    )([R.prop('firstName')(value), R.prop('lastName')(value)])
  }
}

const lastMessagePersonName = (deletedPlaceholderText) =>
  R.compose(
    R.defaultTo(deletedPlaceholderText),
    R.path(['dataLastMessage', 'person', 'first_name']),
  )

const listItemContainer = (
  value,
  classes,
  currentHistoryId,
  onClickListItem,
  deletedPlaceholderText,
  index,
) => {
  const isRead = R.prop('isRead')(value)

  const displayBackground = () => {
    if (currentHistoryId && currentHistoryId === value.historyId) {
      return classes.roomActive
    }

    return isRead ? classes.list : classes.roomNotRead
  }

  return (
    <ListItem
      button
      className={clsx('px-16 py-5', displayBackground())}
      onClick={() => onClickListItem(value, index)}
      key={value.historyId}
    >
      <Grid container>
        <Grid
          item
          xs={1}
          className={clsx(
            classes.gridDot,
            classes.marginAuto,
            'ltr:pr-5 rtl:pl-5',
          )}
        >
          {R.not(isRead) && (
            <Badge
              variant="dot"
              badgeContent={1}
              classes={{ badge: classes.badgeDot }}
            />
          )}
        </Grid>
        <Grid item xs={11}>
          {!R.isNil(value.eventStatus) && (
            <div className={classes.dateEvent}>
              {`${getConversationDateTime(
                value.eventStartDate,
              )} - ${getConversationDateTime(value.eventEndDate)}`}
            </div>
          )}
          <div className={clsx('font-bold', classes.listTitle, 'py-4')}>
            <RichTextDisplay text={value.title} />
          </div>
          <div>
            <Grid container>
              <Grid item xs={2}>
                <PersonAvatar
                  placeHolder={
                    isStartWithEng(R.prop('firstName')(value)) &&
                    isStartWithEng(R.prop('lastName')(value))
                      ? getAvatarPlaceHolder(
                          R.prop('firstName')(value),
                          R.prop('lastName')(value),
                        )
                      : null
                  }
                  subId={R.prop('subscriberId')(value)}
                  src={value.avatar}
                  alt="avatar"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography
                  className={clsx(
                    'mt-4',
                    isRead ? classes.listTextName : classes.listTextNameNotRead,
                  )}
                >
                  {renderSubscriberName(value, deletedPlaceholderText)}
                </Typography>
                <Typography
                  className={clsx(
                    classes.textContent,
                    isRead
                      ? classes.listTextContent
                      : classes.listTextContentNotRead,
                  )}
                >
                  {`${lastMessagePersonName(deletedPlaceholderText)(value)}: ${R.path(
                    ['dataLastMessage', 'lastMessage', 'message'],
                    value,
                  )}`}
                </Typography>
              </Grid>

              <Grid
                item
                xs={4}
                className={clsx(classes.marginAuto, 'text-right')}
              >
                <Typography className={classes.headerDate}>
                  {getConversationDateTime(
                    R.path(
                      ['dataLastMessage', 'lastMessage', 'created'],
                      value,
                    ),
                  )}
                </Typography>
              </Grid>
            </Grid>
          </div>
        </Grid>
      </Grid>
    </ListItem>
  )
}

export default (props) => {
  const classes = styleList()
  const { formatMessage } = useIntl()
  const {
    data,
    isLoading,
    currentHistoryId,
    onClickListItem,
    loadMore,
    hasMore,
  } = props
  const deletedPlaceholderText = formatMessage({
    id: deletedPlaceholderMessageId,
  })

  return (
    <>
      <FuseScrollbars
        className="overflow-auto md:overflow-hidden md:static"
        id="scrollAbleConversationListDiv"
        suppressScrollX={true}
      >
        <InfiniteScroll
          dataLength={data.length}
          className="w-full overflow-hidden static"
          next={loadMore}
          hasMore={hasMore}
          endMessage={
            !R.isEmpty(data) && (
              <div className="w-full text-center text-gray-500">
                <FormattedMessage id="notificationTimeline.endTimeline" />
              </div>
            )
          }
          scrollableTarget="scrollAbleConversationListDiv"
          loader={
            <Loader
              timeout={0}
              isLoading={{ isLoading }}
              placeholder={
                <FuseAnimateGroup
                  enter={{ animation: 'fadeIn' }}
                  leave={{ animation: 'fadeOut' }}
                >
                  <div className="flex justify-center min-w-xs py-64">
                    <CircularProgress color="secondary" />
                  </div>
                </FuseAnimateGroup>
              }
            />
          }
        >
          <List
            className={clsx(
              R.isEmpty(data) && R.not(isLoading) && 'flex h-full mt-192',
              isLoading && 'mt-0',
              'w-full',
            )}
          >
            {R.isEmpty(data) && R.not(isLoading) && listItemEmpty(classes)}
            {data.map((value, index) =>
              listItemContainer(
                value,
                classes,
                currentHistoryId,
                onClickListItem,
                deletedPlaceholderText,
                index,
              ),
            )}
          </List>
        </InfiniteScroll>
      </FuseScrollbars>
    </>
  )
}
